import { Api } from 'api';
import { ChartData } from 'chart.js';
import React from 'react';
// eslint-disable-next-line import/no-unresolved
import { Chart } from 'react-chartjs-2';
import { useMutation, useQuery } from 'react-query';
import {
  AdminSaleReportGraphData,
  ApiResponse,
  ExpertisePoint,
  ExpertisePointGroupTypeLabel,
  ReportingQueryCacheKey,
  SaleReportAdminResponse,
  SaleReportResponse,
  SalesReportDataResponse,
} from 'types';
import {
  StatGroup,
  Center,
  Stat,
  StatHelpText,
  StatLabel,
  StatNumber,
  Text,
  useToast,
  Button,
  Icon,
  Box,
  useColorModeValue,
} from '@chakra-ui/react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
import { hideLoading, showLoading } from 'features';
import { downloadBlobAsFile } from 'core';
import { DownloadIcon } from '@chakra-ui/icons';
import { createColumnHelper } from '@tanstack/react-table';
import { DataTable } from 'components';
import { formatCurrencyWithCommas } from 'core/format-currency';

export interface AdminReportSalesProps {
  beginDate?: Date;
  endDate?: Date;
  expertisePoint?: ExpertisePoint;
  externalPackageGroupSource?: number | null;
  onlyDiscountedSales?: boolean;
  groupType?: number;
}

type SaleReportChartData = ChartData<'line' | 'bar', number[], string>;
type SaleReportQueryData = {
  graphData: SaleReportChartData;
  isEmpty: boolean;
  totalSalesAmount: number;
  totalRoyaltyAmount?: number;
  totalSalesCount: number;
  tableData?: SalesReportDataResponse[];
};

export const AdminReportSales: React.FC<AdminReportSalesProps> = ({
  beginDate,
  endDate,
  expertisePoint,
  externalPackageGroupSource,
  onlyDiscountedSales,
  groupType,
}) => {
  const authUserInfo = useSelector((state: RootState) => state.auth.authUserInfo);
  const toast = useToast();
  const dispatch = useDispatch();

  // Color values for the table
  const tableHeaderBg = useColorModeValue('gray.700', 'gray.600');
  const tableHeaderText = useColorModeValue('white', 'white');

  const { data: reportData, isLoading } = useQuery(
    [
      ReportingQueryCacheKey.GetSalesReport,
      beginDate?.getTime(),
      endDate?.getTime(),
      expertisePoint?.id,
      externalPackageGroupSource,
      onlyDiscountedSales,
      groupType,
    ],
    async (): Promise<SaleReportQueryData | null> => {
      if (beginDate && endDate) {
        const response: ApiResponse<SaleReportAdminResponse | SaleReportResponse> =
          authUserInfo?.isSuperAdmin || authUserInfo?.isAdmin
            ? await Api.Reporting.getSalesReportAdmin(
                beginDate.getTime(),
                endDate.getTime(),
                expertisePoint?.id,
                externalPackageGroupSource,
                onlyDiscountedSales,
                groupType,
              )
            : await Api.Reporting.getSalesReport(
                beginDate.getTime(),
                endDate.getTime(),
                externalPackageGroupSource,
                onlyDiscountedSales,
              );
        const tableDataResponse = await Api.Reporting.getSalesReportData(
          beginDate.getTime(),
          endDate.getTime(),
          expertisePoint?.id,
          groupType,
        );
        const saleData = response.data;
        const graphData: AdminSaleReportGraphData = {
          dateLabels: [],
          saleReportLineData: [],
          countReportBarData: [],
        };
        saleData.sales.forEach((sale) => {
          if (sale.count !== '0') {
            const date = new Date(sale.date);
            graphData.dateLabels.push(
              `${date.getDate()} ${date.toLocaleString(window.navigator.language, { month: 'long' })}`,
            );
            const saleAmount = parseInt(sale.amount, 10);
            graphData.saleReportLineData.push(saleAmount);
            graphData.countReportBarData.push(parseInt(sale.count, 10));
          }
        });
        return {
          isEmpty: graphData.dateLabels.length === 0,
          graphData: {
            labels: graphData.dateLabels,
            datasets: [
              {
                type: 'line' as const,
                label: 'Satış Adeti',
                borderColor: 'rgb(255, 99, 132)',
                borderWidth: 2,
                fill: false,
                data: graphData.countReportBarData,
                yAxisID: 'CountYAxis',
              },
              {
                type: 'bar' as const,
                label: 'Ciro',
                backgroundColor: 'rgb(75, 192, 192)',
                data: graphData.saleReportLineData,
                borderColor: 'white',
                borderWidth: 2,
                yAxisID: 'SalexYAxis',
              },
            ],
          },
          totalSalesAmount: saleData.totalSalesAmount,
          totalSalesCount: saleData.totalSalesCount,
          totalRoyaltyAmount: 'royaltiAmount' in saleData ? saleData.royaltiAmount : undefined,
          tableData: tableDataResponse.data,
        };
      }
      return null;
    },
    {
      retry: 0,
      refetchOnWindowFocus: false,
      enabled: !!(beginDate && endDate),
    },
  );

  const getExportSalesReport = useMutation<any, any>(
    async () => {
      dispatch(showLoading());
      if (beginDate && endDate) {
        const response =
          authUserInfo?.isSuperAdmin || authUserInfo?.isAdmin
            ? await Api.Reporting.exportSalesReportAdmin(
                beginDate?.getTime(),
                endDate.getTime(),
                expertisePoint?.id,
                externalPackageGroupSource,
                onlyDiscountedSales,
                groupType,
              )
            : await Api.Reporting.exportSalesReport(
                beginDate?.getTime(),
                endDate.getTime(),
                externalPackageGroupSource,
              );

        return response;
      }
      return null;
    },
    {
      onSuccess(data) {
        if (data) {
          const date = new Date();
          const formattedDate = `${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()}`;
          const fileName = `Satıslar${formattedDate}.xlsx`;
          downloadBlobAsFile(data, fileName);
        } else {
          toast({
            status: 'info',
            title: 'Rapor Bulunamadı',
            isClosable: true,
          });
        }
      },
      onSettled() {
        dispatch(hideLoading());
      },
      onError() {
        toast({
          status: 'error',
          title: 'Rapor İndirilirken Hata Oluştu',
          isClosable: true,
        });
      },
    },
  );

  // Column helper for DataTable
  const columnHelper = createColumnHelper<SalesReportDataResponse>();

  // Define columns for the DataTable
  const columns = [
    columnHelper.accessor('date', {
      header: 'EKSPERTİZ TARİHİ',
      cell: (info) => info.getValue(),
    }),
    columnHelper.accessor('groupType', {
      header: 'MARKA',
      cell: (info) => {
        const cellGroupType = info.getValue();
        return cellGroupType ? ExpertisePointGroupTypeLabel.get(cellGroupType) || '-' : '-';
      },
    }),
    columnHelper.accessor('expertisePointName', {
      header: 'BAYİ ADI',
      cell: (info) => info.getValue(),
    }),
    columnHelper.accessor('plate', {
      header: 'PLAKA',
      cell: (info) => info.getValue(),
    }),
    columnHelper.accessor('customerName', {
      header: 'MÜŞTERİ ADI',
      cell: (info) => info.getValue(),
    }),
    columnHelper.accessor('packageGroupName', {
      header: 'KAMPANYA ADI',
      cell: (info) => info.getValue() || '-',
    }),

    columnHelper.accessor('packageName', {
      header: 'PAKET ADI',
      cell: (info) => info.getValue(),
    }),
    columnHelper.accessor('listPrice', {
      header: 'LİSTE FİYATI',
      cell: (info) => `${formatCurrencyWithCommas(info.getValue())}`,
      meta: {
        isNumeric: true,
      } as any,
    }),
    columnHelper.accessor('campaignInfo', {
      header: 'KAMPANYA',
      cell: (info) => info.getValue() || '-',
    }),
    columnHelper.accessor('discountAmount', {
      header: 'İNDİRİM TUTARI',
      cell: (info) => `${formatCurrencyWithCommas(info.getValue())}`,
      meta: {
        isNumeric: true,
      } as any,
    }),
    columnHelper.accessor('amount', {
      header: 'SATIŞ TUTAR',
      cell: (info) => `${formatCurrencyWithCommas(info.getValue())} `,
      meta: {
        isNumeric: true,
      } as any,
    }),
    columnHelper.accessor('royalty', {
      header: 'KOMİSYON',
      cell: (info) => `${formatCurrencyWithCommas(info.getValue())}`,
      meta: {
        isNumeric: true,
      } as any,
    }),
  ];

  if (!reportData || isLoading) {
    return null;
  }

  if (reportData.isEmpty) {
    return (
      <Center>
        <Text fontSize="2xl" mt={2} mb={6}>
          Seçtiğiniz Tarih Aralığında Veri Bulunmuyor!
        </Text>
      </Center>
    );
  }

  return (
    <>
      {beginDate && endDate && reportData && reportData.totalSalesAmount && (
        <StatGroup p={4}>
          <Stat>
            <StatLabel>Total Ciro</StatLabel>
            <StatNumber>{formatCurrencyWithCommas(reportData.totalSalesAmount)} </StatNumber>
            <StatHelpText>
              {beginDate.toLocaleDateString()} - {endDate.toLocaleDateString()}
            </StatHelpText>
          </Stat>
          {(authUserInfo?.isSuperAdmin || authUserInfo?.isAdmin) && !!reportData.totalRoyaltyAmount && (
            <Stat>
              <StatLabel>Royalti Bedeli</StatLabel>
              <StatNumber>{formatCurrencyWithCommas(reportData.totalRoyaltyAmount)}</StatNumber>
              <StatHelpText>
                {beginDate.toLocaleDateString()} - {endDate.toLocaleDateString()}
              </StatHelpText>
            </Stat>
          )}
          <Stat>
            <StatLabel>Toplam Satış Adeti</StatLabel>
            <StatNumber>{reportData.totalSalesCount} Adet</StatNumber>
            <StatHelpText>
              {beginDate.toLocaleDateString()} - {endDate.toLocaleDateString()}
            </StatHelpText>
          </Stat>

          <Button variant="solid" leftIcon={<Icon as={DownloadIcon} />} onClick={() => getExportSalesReport.mutate()}>
            Excel İndir
          </Button>
        </StatGroup>
      )}

      {reportData.tableData && reportData.tableData.length > 0 && (
        <Box mt={6} overflowX="auto">
          <DataTable
            data={reportData.tableData}
            columns={columns}
            usePagination
            tableProps={{
              variant: 'simple',
              colorScheme: 'blue',
              size: 'sm',
              sx: {
                'thead tr': {
                  backgroundColor: tableHeaderBg,
                },
                'thead th': {
                  color: tableHeaderText,
                },
              },
            }}
          />
        </Box>
      )}
      <Chart
        type="bar"
        data={reportData.graphData}
        options={{
          scales: {
            SalexYAxis: {
              type: 'linear',
              position: 'left',
            },
            CountYAxis: {
              type: 'linear',
              position: 'right',
              ticks: {
                precision: 0,
              },
              beginAtZero: true,
            },
          },
        }}
      />
    </>
  );
};
